"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  Camera,
  Circle as CircleIcon,
  Eye,
  Mic,
  Pause,
  Play,
  Save,
  Square as SquareIcon,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import {
  WhiteboardRecorder,
  createRecordingFileName,
  humanizeRecordingError,
  isRecordingSupported,
  requestRecordingSaveTarget,
  saveRecordingBlob,
  type RecordingResult,
  type RecordingCaptureMode,
  type RecordingPreview,
  type RecordingSaveResult,
  type RecordingStatus,
  type RecordingToolRailOptions,
  type WebcamPosition,
  type WebcamShape,
  type WebcamSize,
} from "@/lib/recording";

export interface RecordingPanelProps {
  /** Use screen mode to record the complete visible CanvasRoom tab. */
  captureMode?: RecordingCaptureMode;
  /** A mounted board canvas is recorded without showing a screen-share dialog. */
  sourceCanvas?: HTMLCanvasElement | null;
  /** Useful when the canvas lives in a ref and can change between renders. */
  getSourceCanvas?: () => HTMLCanvasElement | null;
  className?: string;
  defaultMicrophoneEnabled?: boolean;
  defaultDisplayAudioEnabled?: boolean;
  defaultCameraEnabled?: boolean;
  toolRail?: RecordingToolRailOptions;
  onStatusChange?: (status: RecordingStatus) => void;
  onRecordingSaved?: (
    recording: RecordingResult,
    save: RecordingSaveResult,
  ) => void;
  onRecordingDiscarded?: () => void;
  onError?: (error: Error) => void;
}

const controlStyle: CSSProperties = {
  alignItems: "center",
  background: "#fff",
  border: "1px solid #ddd9cf",
  borderRadius: 10,
  color: "#282720",
  display: "flex",
  font: "inherit",
  gap: 8,
  minHeight: 38,
  padding: "8px 10px",
};

const labelStyle: CSSProperties = {
  color: "#6b685f",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.075em",
  margin: 0,
  textTransform: "uppercase",
};

const segmentedButtonStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  borderRadius: 7,
  color: "#68655d",
  cursor: "pointer",
  font: "inherit",
  fontSize: 12,
  fontWeight: 650,
  minHeight: 30,
  padding: "5px 9px",
};

function formatElapsed(value: number): string {
  const totalSeconds = Math.max(0, Math.floor(value / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    : `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
}

function statusLabel(status: RecordingStatus, saving: boolean): string {
  if (saving) return "Saving";
  switch (status) {
    case "preparing":
      return "Preparing";
    case "recording":
      return "Recording";
    case "previewing":
      return "Preview ready";
    case "paused":
      return "Paused";
    case "stopping":
      return "Finishing";
    case "error":
      return "Needs attention";
    default:
      return "Ready";
  }
}

function Icon({
  name,
  size = 16,
}: {
  name: "record" | "pause" | "play" | "stop" | "mic" | "camera" | "save" | "preview" | "discard";
  size?: number;
}) {
  const icons: Record<typeof name, LucideIcon> = {
    camera: Camera,
    discard: Trash2,
    mic: Mic,
    pause: Pause,
    play: Play,
    preview: Eye,
    record: CircleIcon,
    save: Save,
    stop: SquareIcon,
  };
  const Glyph = icons[name];
  return (
    <Glyph
      aria-hidden="true"
      fill={name === "record" || name === "stop" ? "currentColor" : "none"}
      size={size}
      strokeWidth={name === "record" || name === "stop" ? 0 : 1.8}
    />
  );
}

function ToggleRow({
  checked,
  disabled,
  icon,
  label,
  onChange,
  supportingText,
}: {
  checked: boolean;
  disabled?: boolean;
  icon: "mic" | "camera";
  label: string;
  onChange: (checked: boolean) => void;
  supportingText: string;
}) {
  return (
    <label
      style={{
        ...controlStyle,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.58 : 1,
        padding: "9px 10px",
      }}
    >
      <span
        style={{
          alignItems: "center",
          background: checked ? "#fbece8" : "#f2f0e9",
          borderRadius: 8,
          color: checked ? "#b83a28" : "#6e6b63",
          display: "flex",
          height: 32,
          justifyContent: "center",
          width: 32,
        }}
      >
        <Icon name={icon} />
      </span>
      <span style={{ display: "grid", flex: 1, gap: 1 }}>
        <span style={{ color: "#24231e", fontSize: 13, fontWeight: 700 }}>
          {label}
        </span>
        <span style={{ color: "#7a776f", fontSize: 11 }}>{supportingText}</span>
      </span>
      <input
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        style={{ accentColor: "#d94c37", height: 17, width: 17 }}
        type="checkbox"
      />
    </label>
  );
}

export function RecordingPanel({
  captureMode = "canvas",
  sourceCanvas,
  getSourceCanvas,
  className,
  defaultMicrophoneEnabled = true,
  defaultDisplayAudioEnabled = true,
  defaultCameraEnabled = true,
  toolRail,
  onStatusChange,
  onRecordingSaved,
  onRecordingDiscarded,
  onError,
}: RecordingPanelProps) {
  const recorderRef = useRef<WhiteboardRecorder | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const previewCameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const selfViewVideoRef = useRef<HTMLVideoElement | null>(null);
  const mountedRef = useRef(false);
  const callbackRef = useRef({
    onStatusChange,
    onRecordingSaved,
    onRecordingDiscarded,
    onError,
  });

  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [microphoneEnabled, setMicrophoneEnabled] = useState(
    defaultMicrophoneEnabled,
  );
  const [cameraEnabled, setCameraEnabled] = useState(defaultCameraEnabled);
  const [displayAudioEnabled, setDisplayAudioEnabled] = useState(
    defaultDisplayAudioEnabled,
  );
  const [webcamShape, setWebcamShape] = useState<WebcamShape>("circle");
  const [webcamPosition, setWebcamPosition] =
    useState<WebcamPosition>("bottom-right");
  const [webcamSize, setWebcamSize] = useState<WebcamSize>("medium");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [supported, setSupported] = useState(true);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [cameraPreviewStream, setCameraPreviewStream] =
    useState<MediaStream | null>(null);
  const [audioPreview, setAudioPreview] =
    useState<RecordingPreview["audio"] | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    callbackRef.current = {
      onStatusChange,
      onRecordingSaved,
      onRecordingDiscarded,
      onError,
    };
  }, [onError, onRecordingDiscarded, onRecordingSaved, onStatusChange]);

  useEffect(() => {
    mountedRef.current = true;
    const supportCheck = window.requestAnimationFrame(() => {
      if (mountedRef.current) setSupported(isRecordingSupported());
    });

    const handleSourceEnded = () => {
      if (mountedRef.current) {
        setSaving(false);
        setSaveMessage("Screen sharing ended. Nothing was saved.");
        callbackRef.current.onRecordingDiscarded?.();
      }
    };

    const recorder = new WhiteboardRecorder({
      onStatusChange: (nextStatus) => {
        if (mountedRef.current) setStatus(nextStatus);
        if (mountedRef.current && nextStatus === "idle") {
          setPreviewStream(null);
          setCameraPreviewStream(null);
          setAudioPreview(null);
          setAudioLevel(0);
        }
        callbackRef.current.onStatusChange?.(nextStatus);
      },
      onError: (error) => {
        if (mountedRef.current) setErrorMessage(humanizeRecordingError(error));
        callbackRef.current.onError?.(error);
      },
      onSourceEnded: handleSourceEnded,
    });
    recorderRef.current = recorder;

    return () => {
      window.cancelAnimationFrame(supportCheck);
      mountedRef.current = false;
      recorder.dispose();
      recorderRef.current = null;
    };
  }, []);

  useEffect(() => {
    const video = previewVideoRef.current;
    if (!video) return;
    video.srcObject = previewStream;
    if (previewStream) void video.play().catch(() => undefined);
    return () => {
      if (video.srcObject === previewStream) video.srcObject = null;
    };
  }, [previewStream]);

  useEffect(() => {
    const videos = [previewCameraVideoRef.current, selfViewVideoRef.current];
    for (const video of videos) {
      if (!video) continue;
      video.srcObject = cameraPreviewStream;
      if (cameraPreviewStream) void video.play().catch(() => undefined);
    }
    return () => {
      for (const video of videos) {
        if (video?.srcObject === cameraPreviewStream) video.srcObject = null;
      }
    };
  }, [cameraPreviewStream, status]);

  useEffect(() => {
    const audioTracks = previewStream?.getAudioTracks() ?? [];
    if (audioTracks.length === 0) return;

    const context = new AudioContext({ sampleRate: 48_000 });
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.72;
    const source = context.createMediaStreamSource(new MediaStream(audioTracks));
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    let frame = 0;
    const updateLevel = () => {
      analyser.getByteTimeDomainData(samples);
      let squareSum = 0;
      for (const sample of samples) {
        const normalized = (sample - 128) / 128;
        squareSum += normalized * normalized;
      }
      setAudioLevel(Math.min(1, Math.sqrt(squareSum / samples.length) * 5));
      frame = requestAnimationFrame(updateLevel);
    };
    void context.resume().catch(() => undefined);
    frame = requestAnimationFrame(updateLevel);

    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      analyser.disconnect();
      void context.close();
    };
  }, [previewStream]);

  useEffect(() => {
    if (status !== "recording" && status !== "paused") {
      return;
    }

    const update = () => setElapsedMs(recorderRef.current?.getElapsedMs() ?? 0);
    update();
    const timer = window.setInterval(update, 250);
    return () => window.clearInterval(timer);
  }, [status]);

  const busy =
    saving || status === "preparing" || status === "stopping";
  const active = status === "recording" || status === "paused";
  const previewing = status === "previewing";
  const sourceSettingsDisabled = busy || active || previewing;
  const appearanceSettingsDisabled = busy || active;

  const recordingOptions = useMemo(
    () => ({
      captureMode,
      sourceCanvas: sourceCanvas ?? getSourceCanvas?.() ?? null,
      includeMicrophone: microphoneEnabled,
      includeCamera: cameraEnabled,
      includeDisplayAudio: displayAudioEnabled,
      frameRate: 60,
      maxWidth: 1920,
      maxHeight: 1080,
      videoBitsPerSecond: 12_000_000,
      audioBitsPerSecond: 256_000,
      webcam: {
        shape: webcamShape,
        position: webcamPosition,
        size: webcamSize,
        mirror: true,
      },
      toolRail: captureMode === "screen" ? undefined : toolRail,
    }),
    [
      cameraEnabled,
      captureMode,
      displayAudioEnabled,
      getSourceCanvas,
      microphoneEnabled,
      sourceCanvas,
      toolRail,
      webcamPosition,
      webcamShape,
      webcamSize,
    ],
  );

  useEffect(() => {
    recorderRef.current?.updateWebcamOverlay(recordingOptions.webcam);
  }, [recordingOptions.webcam]);

  useEffect(() => {
    recorderRef.current?.updateToolRail(
      captureMode === "screen" ? undefined : toolRail,
    );
  }, [captureMode, toolRail]);

  const preparePreview = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    setErrorMessage(null);
    setSaveMessage(null);
    setElapsedMs(0);
    setAudioPreview(null);
    setAudioLevel(0);
    try {
      const preview = await recorder.prepare(recordingOptions);
      if (mountedRef.current) {
        setPreviewStream(preview.stream);
        setCameraPreviewStream(preview.cameraStream ?? null);
        setAudioPreview(preview.audio);
      }
    } catch (error) {
      const value = error instanceof Error ? error : new Error(String(error));
      if (mountedRef.current) setErrorMessage(humanizeRecordingError(value));
    }
  }, [recordingOptions]);

  const start = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    setErrorMessage(null);
    setSaveMessage(null);
    setElapsedMs(0);
    try {
      await recorder.start(recordingOptions);
    } catch (error) {
      // The recorder callback already reports most setup errors, but this also
      // covers misuse errors thrown before the browser capture flow starts.
      const value = error instanceof Error ? error : new Error(String(error));
      setErrorMessage(humanizeRecordingError(value));
    }
  }, [recordingOptions]);

  const resetPreview = useCallback(() => {
    recorderRef.current?.cancelPreview();
    setPreviewStream(null);
    setCameraPreviewStream(null);
    setAudioPreview(null);
    setAudioLevel(0);
    setErrorMessage(null);
  }, []);

  const stopAndSave = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || !active) return;

    setErrorMessage(null);
    setSaveMessage(null);
    setSaving(true);
    try {
      // Both operations are initiated synchronously from this click. That lets
      // Chromium keep the save picker user-activated while MediaRecorder emits
      // its final chunk in the background.
      const recordingPromise = recorder.stop();
      const mimeType = recorder.getMimeType() || "video/webm";
      const targetPromise = requestRecordingSaveTarget({
        suggestedFileName: createRecordingFileName(mimeType),
        mimeType,
      });
      const [recording, target] = await Promise.all([
        recordingPromise,
        targetPromise,
      ]);
      if (target.kind === "cancelled") {
        if (mountedRef.current) {
          setSaveMessage("Recording discarded. Nothing was saved.");
          callbackRef.current.onRecordingDiscarded?.();
        }
        return;
      }
      const save = await saveRecordingBlob(recording.blob, {
        fileName: recording.suggestedFileName,
        target,
      });
      if (mountedRef.current) {
        setSaveMessage(
          save.method === "file-system"
            ? `Saved to ${save.fileName}`
            : `Downloaded ${save.fileName}`,
        );
        callbackRef.current.onRecordingSaved?.(recording, save);
      }
    } catch (error) {
      const value = error instanceof Error ? error : new Error(String(error));
      if (mountedRef.current) setErrorMessage(humanizeRecordingError(value));
      callbackRef.current.onError?.(value);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [active]);

  const discardRecording = useCallback(() => {
    recorderRef.current?.cancel();
    setConfirmDiscard(false);
    setPreviewStream(null);
    setCameraPreviewStream(null);
    setAudioPreview(null);
    setAudioLevel(0);
    setElapsedMs(0);
    setErrorMessage(null);
    setSaveMessage("Recording discarded. Nothing was saved.");
    callbackRef.current.onRecordingDiscarded?.();
  }, []);

  const togglePause = useCallback(() => {
    if (status === "paused") recorderRef.current?.resume();
    else recorderRef.current?.pause();
  }, [status]);

  const hasDirectCanvas =
    captureMode === "canvas" && Boolean(sourceCanvas || getSourceCanvas);
  const sourceDescription = captureMode === "screen"
    ? "Records the complete visible CanvasRoom tab"
    : hasDirectCanvas
    ? "Captures the visible board and pen column"
    : "Choose a tab, window, or screen when recording starts";

  const statusColor =
    status === "recording"
      ? "#d94c37"
      : status === "paused"
        ? "#d58b22"
        : errorMessage
          ? "#bd3e32"
          : "#548367";

  const shapeOptions = useMemo(
    () => [
      { value: "circle" as const, label: "Circle" },
      { value: "square" as const, label: "Square" },
    ],
    [],
  );

  if (active || status === "stopping" || saving) {
    return (
      <>
        {active && cameraPreviewStream ? (
          <video
            aria-label="Live presenter self-view"
            autoPlay
            className={`recording-self-view is-${webcamShape} is-${webcamPosition} is-${webcamSize}`}
            muted
            playsInline
            ref={selfViewVideoRef}
          />
        ) : null}
        <section
          aria-label="Active recording controls"
          className={className}
          style={{
            alignItems: "center",
            background: "rgba(17, 21, 19, 0.96)",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 14,
            boxShadow: "0 14px 38px rgba(17,21,19,0.3)",
            color: "#fff",
            display: "flex",
            gap: 10,
            maxWidth: "none",
            padding: "7px 8px 7px 11px",
            width: "max-content",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              background: status === "paused" ? "#dfa13e" : "#ff7655",
              borderRadius: "50%",
              boxShadow: status === "recording" ? "0 0 0 4px rgba(255,118,85,0.16)" : "none",
              height: 8,
              width: 8,
            }}
          />
          <span
            aria-live="polite"
            style={{
              minWidth: 58,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
              fontVariantNumeric: "tabular-nums",
              fontWeight: 750,
            }}
          >
            {formatElapsed(elapsedMs)}
          </span>
          {active ? (
            confirmDiscard ? (
              <>
                <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>
                  Discard recording?
                </span>
                <button
                  onClick={() => setConfirmDiscard(false)}
                  style={{
                    ...controlStyle,
                    background: "rgba(255,255,255,0.09)",
                    borderColor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: 11,
                    minHeight: 34,
                    padding: "6px 9px",
                  }}
                  type="button"
                >
                  Keep
                </button>
                <button
                  onClick={discardRecording}
                  style={{
                    ...controlStyle,
                    background: "#ff7655",
                    borderColor: "#ff7655",
                    color: "#111513",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 800,
                    minHeight: 34,
                    padding: "6px 10px",
                  }}
                  type="button"
                >
                  Discard now
                </button>
              </>
            ) : (
              <>
                <button
                  aria-label={status === "paused" ? "Resume recording" : "Pause recording"}
                  onClick={togglePause}
                  style={{
                    ...controlStyle,
                    background: "rgba(255,255,255,0.09)",
                    borderColor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    cursor: "pointer",
                    minHeight: 34,
                    padding: "6px 9px",
                  }}
                  type="button"
                >
                  <Icon name={status === "paused" ? "play" : "pause"} />
                </button>
                <button
                  aria-label="Discard recording"
                  onClick={() => setConfirmDiscard(true)}
                  style={{
                    ...controlStyle,
                    background: "rgba(255,255,255,0.09)",
                    borderColor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    cursor: "pointer",
                    minHeight: 34,
                    padding: "6px 9px",
                  }}
                  type="button"
                >
                  <Icon name="discard" />
                </button>
                <button
                  onClick={() => void stopAndSave()}
                  style={{
                    ...controlStyle,
                    background: "#ff7655",
                    borderColor: "#ff7655",
                    color: "#111513",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 800,
                    minHeight: 34,
                    padding: "6px 10px",
                  }}
                  type="button"
                >
                  <Icon name="stop" /> Stop &amp; save
                </button>
              </>
            )
          ) : (
            <span style={{ color: "#c5ccc8", fontSize: 10, padding: "0 7px" }}>
              {saving ? "Saving recording…" : "Finishing…"}
            </span>
          )}
        </section>
      </>
    );
  }

  return (
    <section
      aria-label="Record whiteboard"
      className={className}
      style={{
        background: "#fbfaf6",
        border: "1px solid #e4e0d6",
        borderRadius: 16,
        boxShadow: "0 16px 42px rgba(39, 35, 26, 0.12)",
        boxSizing: "border-box",
        color: "#24231e",
        fontFamily:
          "var(--font-sans), Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
        maxWidth: 360,
        padding: 14,
        width: "100%",
      }}
    >
      <header
        style={{
          alignItems: "center",
          display: "flex",
          gap: 10,
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <div style={{ display: "grid", gap: 2 }}>
          <h2 style={{ fontSize: 15, lineHeight: 1.2, margin: 0 }}>Record board</h2>
          <span style={{ color: "#77736a", fontSize: 11 }}>
            {sourceDescription}
          </span>
        </div>
        <div
          aria-live="polite"
          style={{
            alignItems: "center",
            background: "#f1eee7",
            borderRadius: 999,
            color: "#5e5a52",
            display: "flex",
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 700,
            gap: 6,
            padding: "6px 8px",
          }}
        >
          <span
            style={{
              background: statusColor,
              borderRadius: "50%",
              boxShadow: previewing ? `0 0 0 3px ${statusColor}26` : "none",
              height: 7,
              width: 7,
            }}
          />
          {statusLabel(status, saving)}
        </div>
      </header>

      <div
        aria-label="Recording preview"
        style={{
          aspectRatio: "16 / 9",
          background: "#1d211f",
          border: "1px solid #d7d3ca",
          borderRadius: 12,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
          marginBottom: 12,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <video
          aria-label="Exact screen recording preview"
          autoPlay
          muted
          playsInline
          ref={previewVideoRef}
          style={{
            display: previewStream ? "block" : "none",
            height: "100%",
            objectFit: "contain",
            width: "100%",
          }}
        />
        {previewStream && cameraPreviewStream ? (
          <video
            aria-label="Live camera placement preview"
            autoPlay
            muted
            playsInline
            ref={previewCameraVideoRef}
            style={{
              aspectRatio: "1 / 1",
              border: "2px solid rgba(255,255,255,0.96)",
              borderRadius: webcamShape === "circle" ? "50%" : 10,
              boxShadow: "0 5px 16px rgba(0,0,0,0.28)",
              height:
                webcamSize === "small"
                  ? "26%"
                  : webcamSize === "large"
                    ? "42%"
                    : "34%",
              objectFit: "cover",
              position: "absolute",
              ...(webcamPosition.startsWith("top") ? { top: 9 } : { bottom: 9 }),
              ...(webcamPosition.endsWith("left") ? { left: 9 } : { right: 9 }),
              transform: "scaleX(-1)",
            }}
          />
        ) : null}
        {!previewStream ? (
          <div
            style={{
              alignItems: "center",
              color: "#d9dedb",
              display: "flex",
              flexDirection: "column",
              gap: 7,
              inset: 0,
              justifyContent: "center",
              padding: 20,
              position: "absolute",
              textAlign: "center",
            }}
          >
            <span
              style={{
                alignItems: "center",
                background: "rgba(255,255,255,0.09)",
                borderRadius: 10,
                display: "flex",
                height: 36,
                justifyContent: "center",
                width: 36,
              }}
            >
              <Icon name="preview" />
            </span>
            <strong style={{ fontSize: 12 }}>Preview before you record</strong>
            <span style={{ color: "#9ea7a2", fontSize: 10, lineHeight: 1.4 }}>
              {captureMode === "screen"
                ? "Choose the current CanvasRoom tab, then check the whole app and your camera placement."
                : "Check the visible board, pen column, and your camera placement first."}
            </span>
          </div>
        ) : null}
        <span
          style={{
            background: "rgba(17,21,19,0.78)",
            borderRadius: 999,
            bottom: 8,
            color: "#f7f7f2",
            fontSize: 9,
            fontWeight: 700,
            left: 8,
            letterSpacing: "0.04em",
            padding: "5px 7px",
            position: "absolute",
            textTransform: "uppercase",
          }}
        >
          Exact recorded area
        </span>
      </div>

      {previewing ? (
        <div
          style={{
            alignItems: "center",
            color: "#626a65",
            display: "flex",
            fontSize: 10,
            justifyContent: "space-between",
            margin: "-4px 2px 10px",
          }}
        >
          <span style={{ display: "grid", gap: 2 }}>
            <span>
              {captureMode === "screen"
                ? "Whole CanvasRoom tab and camera are ready."
                : "Camera and board are ready."}
            </span>
            <strong style={{ color: audioPreview?.enabled ? "#47755a" : "#a1483d" }}>
              {audioPreview?.enabled
                ? `Sound ready${audioPreview.microphoneLabel ? ` · ${audioPreview.microphoneLabel}` : ""}${audioPreview.systemAudioIncluded ? " + shared audio" : ""}${audioPreview.sampleRate ? ` · ${Math.round(audioPreview.sampleRate / 1_000)} kHz` : ""}`
                : "No sound track detected"}
            </strong>
            {audioPreview?.enabled ? (
              <span
                aria-label="Live microphone and shared-audio level"
                style={{
                  background: "#d9ddd8",
                  borderRadius: 999,
                  display: "block",
                  height: 4,
                  overflow: "hidden",
                  width: 92,
                }}
              >
                <span
                  style={{
                    background: audioLevel > 0.82 ? "#d94c37" : "#548367",
                    display: "block",
                    height: "100%",
                    transition: "width 80ms linear",
                    width: `${Math.max(3, audioLevel * 100)}%`,
                  }}
                />
              </span>
            ) : null}
          </span>
          <button
            onClick={resetPreview}
            style={{
              background: "transparent",
              border: 0,
              color: "#4d60c9",
              cursor: "pointer",
              fontSize: 10,
              fontWeight: 700,
              padding: 3,
            }}
            type="button"
          >
            Change setup
          </button>
        </div>
      ) : null}

      {active || busy ? (
        <div
          style={{
            alignItems: "center",
            background: "#24231e",
            borderRadius: 12,
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 12,
            padding: "11px 12px",
          }}
        >
          <div style={{ alignItems: "center", display: "flex", gap: 9 }}>
            <span
              style={{
                background: "#e2543e",
                borderRadius: "50%",
                height: 9,
                width: 9,
              }}
            />
            <span
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 17,
                fontVariantNumeric: "tabular-nums",
                fontWeight: 700,
                letterSpacing: "0.035em",
              }}
            >
              {formatElapsed(elapsedMs)}
            </span>
          </div>
          <span style={{ color: "#bbb7af", fontSize: 10 }}>
            1080p · 60 fps · 256 kbps audio
          </span>
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 8 }}>
        <ToggleRow
          checked={microphoneEnabled}
          disabled={sourceSettingsDisabled}
          icon="mic"
          label="Microphone"
          onChange={setMicrophoneEnabled}
          supportingText="Use the system-default wired, Bluetooth, or built-in mic"
        />
        <ToggleRow
          checked={cameraEnabled}
          disabled={sourceSettingsDisabled}
          icon="camera"
          label="Camera overlay"
          onChange={setCameraEnabled}
          supportingText="Add a presenter bubble"
        />

        {!hasDirectCanvas ? (
          <label
            style={{
              alignItems: "center",
              color: "#6e6b63",
              cursor: sourceSettingsDisabled ? "not-allowed" : "pointer",
              display: "flex",
              fontSize: 12,
              gap: 8,
              opacity: sourceSettingsDisabled ? 0.58 : 1,
              padding: "2px 3px",
            }}
          >
            <input
              checked={displayAudioEnabled}
              disabled={sourceSettingsDisabled}
              onChange={(event) => setDisplayAudioEnabled(event.target.checked)}
              style={{ accentColor: "#d94c37" }}
              type="checkbox"
            />
            Include shared tab/system sound when available
          </label>
        ) : null}
      </div>

      {cameraEnabled ? (
        <div
          aria-label="Camera overlay settings"
          style={{
            borderTop: "1px solid #e4e0d6",
            display: "grid",
            gap: 10,
            marginTop: 13,
            paddingTop: 12,
          }}
        >
          <p style={labelStyle}>Camera appearance</p>
          <div
            style={{
              ...controlStyle,
              background: "#efede6",
              border: 0,
              gap: 2,
              padding: 4,
            }}
          >
            {shapeOptions.map((option) => {
              const selected = webcamShape === option.value;
              return (
                <button
                  aria-pressed={selected}
                  disabled={appearanceSettingsDisabled}
                  key={option.value}
                  onClick={() => setWebcamShape(option.value)}
                  style={{
                    ...segmentedButtonStyle,
                    background: selected ? "#fff" : "transparent",
                    boxShadow: selected
                      ? "0 1px 3px rgba(35, 32, 24, 0.12)"
                      : "none",
                    color: selected ? "#24231e" : "#747169",
                    flex: 1,
                    opacity: appearanceSettingsDisabled ? 0.55 : 1,
                  }}
                  type="button"
                >
                  {option.value === "circle" ? (
                    <CircleIcon
                      aria-hidden="true"
                      size={12}
                      style={{ marginRight: 6, verticalAlign: -2 }}
                    />
                  ) : (
                    <SquareIcon
                      aria-hidden="true"
                      size={12}
                      style={{ marginRight: 6, verticalAlign: -2 }}
                    />
                  )}
                  {option.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr" }}>
            <label style={{ display: "grid", gap: 5 }}>
              <span style={labelStyle}>Corner</span>
              <select
                disabled={appearanceSettingsDisabled}
                onChange={(event) =>
                  setWebcamPosition(event.target.value as WebcamPosition)
                }
                style={{
                  ...controlStyle,
                  appearance: "auto",
                  opacity: appearanceSettingsDisabled ? 0.55 : 1,
                  width: "100%",
                }}
                value={webcamPosition}
              >
                <option value="top-left">Top left</option>
                <option value="top-right">Top right</option>
                <option value="bottom-left">Bottom left</option>
                <option value="bottom-right">Bottom right</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: 5 }}>
              <span style={labelStyle}>Size</span>
              <select
                disabled={appearanceSettingsDisabled}
                onChange={(event) => setWebcamSize(event.target.value as WebcamSize)}
                style={{
                  ...controlStyle,
                  appearance: "auto",
                  opacity: appearanceSettingsDisabled ? 0.55 : 1,
                  width: "100%",
                }}
                value={webcamSize}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </label>
          </div>
        </div>
      ) : null}

      {!supported ? (
        <p
          role="alert"
          style={{ color: "#a83c31", fontSize: 12, lineHeight: 1.45, margin: "12px 2px 0" }}
        >
          This browser cannot record media. Try the latest Chrome, Edge, or Safari.
        </p>
      ) : null}
      {errorMessage ? (
        <p
          role="alert"
          style={{
            background: "#fff0ec",
            border: "1px solid #f0c7bf",
            borderRadius: 9,
            color: "#963528",
            fontSize: 12,
            lineHeight: 1.45,
            margin: "12px 0 0",
            padding: "8px 10px",
          }}
        >
          {errorMessage}
        </p>
      ) : null}
      {saveMessage && !errorMessage ? (
        <p
          aria-live="polite"
          style={{
            alignItems: "center",
            color: "#47755a",
            display: "flex",
            fontSize: 12,
            gap: 6,
            margin: "12px 2px 0",
          }}
        >
          <Icon name="save" size={15} />
          {saveMessage}
        </p>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        {!active ? (
          <button
            disabled={!supported || busy}
            onClick={() => void (previewing ? start() : preparePreview())}
            style={{
              alignItems: "center",
              background: busy ? "#e4b1a8" : "#d94c37",
              border: 0,
              borderRadius: 10,
              color: "#fff",
              cursor: !supported || busy ? "not-allowed" : "pointer",
              display: "flex",
              flex: 1,
              font: "inherit",
              fontSize: 13,
              fontWeight: 750,
              gap: 8,
              justifyContent: "center",
              minHeight: 42,
              padding: "9px 13px",
            }}
            type="button"
          >
            <Icon name={previewing ? "record" : "preview"} />
            {saving
              ? "Saving…"
              : status === "preparing"
                ? "Preparing preview…"
                : previewing
                  ? "Start recording"
                  : "Preview setup"}
          </button>
        ) : (
          <>
            <button
              disabled={busy}
              onClick={togglePause}
              style={{
                ...controlStyle,
                cursor: busy ? "not-allowed" : "pointer",
                flex: 1,
                fontSize: 13,
                fontWeight: 700,
                justifyContent: "center",
              }}
              type="button"
            >
              <Icon name={status === "paused" ? "play" : "pause"} />
              {status === "paused" ? "Resume" : "Pause"}
            </button>
            <button
              disabled={busy}
              onClick={() => void stopAndSave()}
              style={{
                ...controlStyle,
                background: "#24231e",
                borderColor: "#24231e",
                color: "#fff",
                cursor: busy ? "not-allowed" : "pointer",
                flex: 1,
                fontSize: 13,
                fontWeight: 700,
                justifyContent: "center",
              }}
              type="button"
            >
              <Icon name="stop" />
              Stop &amp; save
            </button>
          </>
        )}
      </div>

      <p
        style={{
          color: "#8a867d",
          fontSize: 10,
          lineHeight: 1.45,
          margin: "9px 2px 0",
          textAlign: "center",
        }}
      >
        {captureMode === "screen"
          ? "For the complete interface, choose This Tab and select the CanvasRoom tab. Recording starts only after you confirm."
          : "Preview grants camera and microphone access, but recording starts only after you confirm."}
      </p>
    </section>
  );
}

export default RecordingPanel;
